import dayjs from 'dayjs';
import { type UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router';

import type { ActivityType } from '~/api/common/types';
import type { DocumentFormLine } from '~/features/documents';
import type { routes } from '~/providers/RouterProvider/router.routes';
import type { Params } from '~/providers/RouterProvider/router.types';

import { useClients } from '~/api/clients';
import { useConditions } from '~/api/conditions';
import { useUser } from '~/api/user';
import { CommandMenuItem } from '~/providers/CommandMenuProvider/CommandMenuItem';
import { uuidv4 } from '~/utils/string';

import type { CreateEditQuotationFormType } from './types';

type Props = {
  form: UseFormReturn<CreateEditQuotationFormType>;
};

const getRandom = <T,>(array: T[]): T | undefined => array[Math.floor(Math.random() * array.length)];

export const QuotationFormCommandMenuItems = ({ form: { setValue } }: Props) => {
  const { quotationId } = useParams<Params<typeof routes.createQuotation | typeof routes.editQuotation>>();

  const { data: clients } = useClients();
  const { data: conditions } = useConditions();

  const { data: user } = useUser();

  return (
    <CommandMenuItem
      group="Quotation"
      label="Fill input fields"
      onClick={() => {
        const options = { shouldDirty: true, shouldTouch: true, shouldValidate: true };

        const title = `Quotation #${quotationId}`;
        const coEntryNumber = `OFF_${dayjs().unix()}`;
        const client = getRandom(clients) ?? null;
        const clientId = client?.id ?? null;
        const contactId = getRandom(client?.contacts.filter(({ deleted }) => !deleted) ?? [])?.id ?? null;
        const expirationDate = dayjs()
          .add(Math.ceil(Math.random() * 30) + 14, 'days')
          .toDate();
        const brandId = Math.random() > 0.5 ? null : (getRandom(user.brands)?.id ?? null);
        const conditionId = getRandom(conditions)?.id ?? null;
        const lines = Array.from({ length: Math.ceil(Math.random() * 3) }, (_, i) => {
          const activityType: ActivityType =
            i === 0
              ? 'CreativeGeneral'
              : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                getRandom([
                  'CreativeGeneral',
                  'NonCreative',
                  'CreativeReuse',
                  'CreativeSubcontracting',
                  'CreativeComputer',
                ] satisfies ActivityType[])!;

          const price = Math.ceil(Math.random() * 50) * 10;
          const quantity = Math.ceil(Math.random() * 3);

          return {
            uuid: uuidv4(),
            type: 'entry',
            description: `Line ${i + 1}`,
            activityType,
            price,
            discountType: null,
            discountValue: null,
            quantity,
            unit: null,
            productId: null,
          } satisfies DocumentFormLine;
        });
        const sections = [{ name: '', isPhotoAlbum: false, lines }];

        setValue('title', title, options);
        setValue('coEntryNumber', coEntryNumber, options);
        setValue('clientId', clientId, options);
        setValue('contactId', contactId, options);
        setValue('expirationDate', expirationDate, options);
        setValue('brandId', brandId, options);
        setValue('condition', conditionId ? { id: conditionId, type: 'condition' } : null, options);
        setValue('sections', sections, options);
      }}
    />
  );
};
